import {ThunkAction, ThunkDispatch} from 'redux-thunk';
import {IStore} from 'redux/interface';
import {Action} from 'redux';
import {Auth0Client} from '@auth0/auth0-spa-js';
import {IUser} from './interfaces';
import {IUserProfile} from '@wholesalechange/chatcomponent';
import {
  clearUserAction,
  emailNotVerifiedAction,
  finishAuthorizationAction,
  loginErrorActionClear,
  loginErrorActionSet,
  setSiteSettingsAction,
  setUserAction,
  setUserGroupsAction,
  setUserLanguageAction,
  setUserNicknameAction,
  setUserProfileAction,
  setVerificationEmailAction,
  setVideoAvatarAction
} from './actions';
import {setAxiosHeaders} from 'redux/api/helpers';
import {
  getUserGroupsApi,
  getUserProfileApi,
  getUserSiteSettingsApi,
  updateUserProfileApi,
  updateUserProfileNicknameApi, updateUserVideoAvatarApi,
  detectUserTimezoneApi,
  setUserTimezoneApi
} from 'redux/api/user';
import {getBrowserTimezone} from 'utils/helpers/timezone';
import {getVideoAvatarByIdApi} from 'redux/api/media';
import {loadMediaAction} from 'redux/media/mediaAsyncActions';
import {errorHandler} from 'redux/errorHandler';
import {changeLocale} from '../translation/translationAsyncActions';
import {ToastKey} from 'components/gritx-toast/ToastKeyEnum';
import {clearErrorAction, finishLoadingAction, startLoadingAction} from '../status/actions';
import {
  getNotificationCount,
  getUserData,
  getUserGroups,
  updateProfile, updateUserVideoAvatar
} from '../pageAsyncActions';
import {SystemLanguage} from '../../utils/constants/constants';
import {getFavoritesNewItemsAction} from '../content/contentAsyncActions';
import {getFavoritesNewItemsApi} from '../api/content';
import {favoriteNewItemsLoaded} from '../content/actions';

const loginErrorHandler = async ({
  dispatch,
  auth0Client
}: { dispatch: ThunkDispatch<IStore, unknown, Action>, auth0Client: Auth0Client }) => {
  try {
    await auth0Client.logout({
      logoutParams: {
        returnTo: document.location.origin
      }
    });
  } catch (e) {
    console.error('loginErrorHandler error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
  }
  dispatch(loginErrorActionSet());
};

export const startLoginAction = (appRedirectUri?: string): ThunkAction<void, IStore, unknown, Action> => async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();

  dispatch(loginErrorActionClear());
  try {
    let queryString = '';

    if (appRedirectUri) {
      const searchParams = new URLSearchParams();

      searchParams.append('appRedirectUri', appRedirectUri);
      queryString = searchParams.toString();
    }
    await auth0Client.loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
        redirect_uri: `${window.location.origin}/callback${queryString && `?${queryString}`}`
      }
    });
  } catch (e) {
    console.error('startLogin error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
    await loginErrorHandler({
      dispatch,
      auth0Client
    });
  }
};

export const loadVideoAvatarAction = (videoAvatarId: number): ThunkAction<void, IStore, unknown, Action> => async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();

  if (!isAuthenticated) {
    return;
  }

  try {
    const {data} = await getVideoAvatarByIdApi(videoAvatarId);

    dispatch(setVideoAvatarAction(data));
  } catch (e) {
    dispatch(setVideoAvatarAction(null));
  }
};

// UCSF-3519: fire-and-forget on each profile load; the server stores the zone only while the user has
// none, so repeating it is harmless. A failure is logged and never affects sign-in.
export const reportBrowserTimezone = (): void => {
  const timezone = getBrowserTimezone();

  if (!timezone) {
    return;
  }
  Promise.resolve()
    .then(() => detectUserTimezoneApi(timezone))
    .catch((e) => {
      console.error('reportBrowserTimezone error', {
        message: e?.message,
        response: e?.response?.data
      });
    });
};

export const getUserDataAction = (): ThunkAction<void, IStore, unknown, Action> => async (dispatch) => {
  const actionKey = getUserData;

  try {
    dispatch(startLoadingAction(actionKey));
    const {data: userData} = await getUserProfileApi();

    reportBrowserTimezone();
    const {data: userSettings} = await getUserSiteSettingsApi();
    const {data} = await getFavoritesNewItemsApi();

    if (userData?.userAvatarId) {
      dispatch(loadMediaAction(userData.userAvatarId));
    }

    if (userData?.dialogAvatarId) {
      dispatch(loadMediaAction(userData.dialogAvatarId));
    }

    if (userData?.videoAvatarId && userSettings?.siteSettings?.profileSettings?.useVideoAvatars) {
      dispatch(loadVideoAvatarAction(userData.videoAvatarId));
    }

    if (userData?.language && userSettings?.siteSettings?.languages?.includes(userData.language)) {
      dispatch(changeLocale(userData.language));

      dispatch(setUserLanguageAction(userData));
    } else {
      dispatch(setUserLanguageAction({...userData, language: SystemLanguage.English}));
    }

    dispatch(setSiteSettingsAction(userSettings.siteSettings));

    dispatch(favoriteNewItemsLoaded(data));
  } catch (e) {
    console.error('getUserData error', {
      message: e.message,
      status: e.status,
      response: e.response?.data,
      stack: e.stack
    });
    dispatch(errorHandler({
      toastKey: ToastKey.GetUserData,
      actionKey,
      error: e,
      currentAction: getUserDataAction()
    }));
  } finally {
    dispatch(finishLoadingAction(actionKey));
  }
};

export const updateUserProfileAction = (profile: IUserProfile): ThunkAction<void, IStore, unknown, Action> => async (dispatch) => {
  const actionKey = updateProfile;

  dispatch(startLoadingAction(actionKey));
  try {
    const {data} = await updateUserProfileApi(profile);

    dispatch(setUserProfileAction(data));
    dispatch(finishLoadingAction(actionKey));
  } catch (e) {
    console.error('updateUserProfileAction error', {
      message: e.message,
      status: e.status,
      response: e.response?.data,
      stack: e.stack
    });
    dispatch(errorHandler({
      toastKey: ToastKey.UpdateUserProfileAction,
      actionKey,
      error: e
    }));
  }
};

export const updateUserVideoAvatarAction = (avatarId: number): ThunkAction<void, IStore, unknown, Action> => async (dispatch) => {
  const actionKey = updateUserVideoAvatar;

  dispatch(startLoadingAction(actionKey));
  try {
    const {data} = await updateUserVideoAvatarApi(avatarId);

    dispatch(finishLoadingAction(actionKey));
  } catch (e) {
    console.error('updateUserVideoAvatarAction error', {
      message: e.message,
      status: e.status,
      response: e.response?.data,
      stack: e.stack
    });
    dispatch(errorHandler({
      toastKey: ToastKey.UpdateUserVideoAvatarAction,
      actionKey,
      error: e
    }));
  }
};

// UCSF-3519: saves the timezone chosen on the profile page; null resets it to "Automatic".
export const updateUserTimezoneAction = (timezone: string | null): ThunkAction<Promise<boolean>, IStore, unknown, Action> => async (dispatch) => {
  try {
    await setUserTimezoneApi(timezone);

    return true;
  } catch (e) {
    console.error('updateUserTimezoneAction error', {
      message: e.message,
      status: e.status,
      response: e.response?.data,
      stack: e.stack
    });
    dispatch(errorHandler({
      toastKey: ToastKey.UpdateUserProfileAction,
      actionKey: 'updateUserTimezoneAction',
      error: e
    }));

    return false;
  }
};

export const updateUserNickAction = (profile: IUserProfile): ThunkAction<void, IStore, unknown, Action> => async (dispatch) => {
  try {
    const {data} = await updateUserProfileNicknameApi(profile.nickname);

    if (data) {
      dispatch(setUserNicknameAction(profile));
    }
  } catch (e) {
    console.error('setUserNicknameAction error', {
      message: e.message,
      status: e.status,
      response: e.response?.data,
      stack: e.stack
    });
    dispatch(errorHandler({
      toastKey: ToastKey.UpdateUserProfileAction,
      actionKey: 'setUserNicknameAction',
      error: e
    }));
  }
};

export const finishLoginAction = (): ThunkAction<void, IStore, unknown, Action> => async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();

  try {
    await auth0Client.handleRedirectCallback();
    const user = await auth0Client.getUser() as IUser;
    const token = await auth0Client.getTokenSilentlyWithFallback();

    setAxiosHeaders(user, token);

    dispatch(loginErrorActionClear());
    dispatch(setUserAction(user));
    dispatch(getUserDataAction());
  } catch (e) {
    if (e.error === 'access_denied' && e.message.startsWith('ERR_EMAIL_NOT_VERIFIED')) {
      dispatch(emailNotVerifiedAction());
      const email: string = e.message.split('ERR_EMAIL_NOT_VERIFIED: ')[1];

      console.log('ERR_EMAIL_NOT_VERIFIED: ', email);

      dispatch(setVerificationEmailAction(email.trim()));

      return;
    }
    console.error('finishLogin error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
    await loginErrorHandler({
      dispatch,
      auth0Client
    });
  }
};

export const logoutAction = (): ThunkAction<void, IStore, unknown, Action> => async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();

  dispatch(loginErrorActionClear());

  try {
    await auth0Client.logout({
      logoutParams: {
        returnTo: document.location.origin
      }
    });
    dispatch(clearUserAction());
  } catch (e) {
    console.error('logoutAction error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
    await loginErrorHandler({
      dispatch,
      auth0Client
    });
  }
};

export const getAuthorizationAction = (): ThunkAction<void, IStore, unknown, Action> => async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();

  dispatch(loginErrorActionClear());

  try {
    const isAuthenticated = await auth0Client.isAuthenticated();

    if (isAuthenticated) {
      const user = await auth0Client.getUser() as IUser;
      const token = await auth0Client.getTokenSilentlyWithFallback();

      setAxiosHeaders(user, token);
      dispatch(setUserAction(user as IUser));
      dispatch(getUserDataAction());
      dispatch(getFavoritesNewItemsAction());
    }
    dispatch(finishAuthorizationAction());
  } catch (e) {
    console.error('getAuthorizationAction error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
    await loginErrorHandler({
      dispatch,
      auth0Client
    });
  }
};

export const loadUserGroupsAction = ():
  ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {
    auth: {
      auth0Client
    }
  } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getUserGroups;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));

    try {
      const {data} = await getUserGroupsApi();

      dispatch(finishLoadingAction(actionKey));
      dispatch(clearErrorAction(actionKey));

      dispatch(setUserGroupsAction(data));
    } catch (e) {
      console.log('loadUserGroupsAction', e.message);
      dispatch(errorHandler({
        toastKey: ToastKey.LoadUserGroupsAction,
        actionKey,
        error: e,
        currentAction: loadUserGroupsAction()
      }));
    }
  }
});
